import axios from 'axios';
import { useAuthStore } from '../stores/authStore';

// Resolve the API base URL with the following priority:
//   1. Runtime config injected at container start (window.__NEURO_RUNTIME_CONFIG__)
//   2. Vite build-time env var (import.meta.env.VITE_API_URL)
//   3. Empty string — relative paths, same-origin (local dev / nginx proxy)
const runtimeUrl =
  typeof window !== 'undefined' &&
  (window as any).__NEURO_RUNTIME_CONFIG__?.VITE_API_URL;
function normalizeApiBaseUrl(rawUrl: string | undefined): string {
  const value = (rawUrl ?? '').trim().replace(/\/+$/, '');

  // Frontend API callers already include the /api prefix, e.g.
  // client.get('/api/plugins').  Deployment configs often set VITE_API_URL to
  // "https://host/api"; using that verbatim produces "https://host/api/api/..."
  // and prevents plugins (including Chat) from loading.  Treat a trailing /api
  // as the API prefix, not part of the axios base URL.
  return value.replace(/\/api$/i, '');
}

function resolveApiBaseUrl(): string {
  // In Vite development, prefer same-origin requests when the dev proxy is
  // configured. This avoids CORS preflights to the backend and lets
  // vite.config.ts proxy /api, /ws, and Socket.IO correctly.
  if (!runtimeUrl && import.meta.env.DEV && import.meta.env.VITE_PROXY_TARGET) {
    return '';
  }

  return normalizeApiBaseUrl(runtimeUrl || import.meta.env.VITE_API_URL || '');
}

export const API_BASE: string = resolveApiBaseUrl();

const client = axios.create({
  baseURL: API_BASE,
  // Increase default timeout to 45 seconds to accommodate longer AI requests
  timeout: 45000,
  // Enable cookies for httpOnly token authentication
  withCredentials: true,
});

client.interceptors.request.use((config) => {
  // During migration: still send Authorization header if token exists in store
  // After backend fully supports httpOnly cookies, this can be removed
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers = config.headers || {};
    (config.headers as any).Authorization = `Bearer ${token}`;
  }
  return config;
});

client.interceptors.response.use(
  (res) => {
    // Unwrap the response envelope if present
    // Backend wraps responses in: { ok, data, error, meta }
    if (res.data && typeof res.data === 'object' && 'ok' in res.data && 'data' in res.data) {
      // Preserve error info if the envelope indicates failure
      if (res.data.ok === false && res.data.error) {
        const err = new Error(res.data.error.message || 'Request failed');
        (err as any).code = res.data.error.code;
        (err as any).meta = res.data.meta;
        throw err;
      }
      res.data = res.data.data;
    }
    return res;
  },
  (err) => {
    // Handle 401 by clearing auth and redirecting to login
    if (err?.response?.status === 401) {
      useAuthStore.getState().clearAuth();
      // Only redirect if not already on login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

export default client;
